import type { HomeSkill, SkillEvidence } from '@/lib/api/home'

/**
 * How much of a skill's solving we can positively vouch for: recorded, and not
 * arrived whole.
 *
 * Everything else is unaccounted for rather than doubted. Mirrors
 * SkillEvidence.AccountedFor in the Go service, which is the definition the
 * score is stored against.
 */
export function accountedFor(ev: SkillEvidence): number {
  // Counted server-side per solve, so a solve that is both recorded clean and
  // explained is not double counted. Falls back to the pre-explaining
  // definition for evidence written before that column existed.
  if (ev.solves_accounted > 0) return ev.solves_accounted
  return Math.max(0, ev.solves_recorded - ev.solves_pasted)
}

/**
 * Whether we hold solve telemetry for this student at all.
 *
 * This gates the entire per-skill treatment, and it matters more than it
 * sounds. Telemetry only records work done since it shipped, so for every
 * student with history — all of them, on the day this deploys — each skill
 * would otherwise render as a fully shaded bar with a number beside it and no
 * solid fill anywhere. That reads as a broken chart, not as a caveat, and it
 * repeats the same apology on every row.
 *
 * When there is no record at all, the honest thing is one sentence about the
 * gap, said once, and bars that simply show the score.
 */
export function hasAnyRecord(skills: HomeSkill[]): boolean {
  return skills.some(
    (s) => (s.evidence?.solves_recorded ?? 0) > 0 || (s.evidence?.solves_explained ?? 0) > 0,
  )
}

/**
 * Whether this skill has solves it cannot account for, and so needs its bar
 * split. Always false before we have any record, per hasAnyRecord.
 */
export function needsCaveat(ev: SkillEvidence, anyRecord: boolean): boolean {
  if (!anyRecord) return false
  return ev.coding_solved > 0 && accountedFor(ev) < ev.coding_solved
}

/**
 * The sentence under the bar.
 *
 * Four distinct situations, deliberately worded so none can be mistaken for
 * another:
 *
 *  - nothing solved yet — a denominator, not a doubt
 *  - solved, but we were not recording yet — stated once at the card, not here
 *  - solved and unrecorded while others were recorded — our blind spot, ours
 *  - solved and pasted — the only case naming the student's own work, and even
 *    then as something to revisit rather than to answer for
 *
 * Returns null when there is nothing worth qualifying, so a clean skill renders
 * clean rather than carrying a reassurance nobody asked for.
 */
export function coverageNote(ev: SkillEvidence, anyRecord: boolean): string | null {
  if (ev.coding_total === 0) return null

  if (ev.coding_solved === 0) {
    return `None of the ${ev.coding_total} problems set for this skill solved yet.`
  }

  const solvedOf = `${ev.coding_solved} of ${ev.coding_total} problems solved`

  // Before any telemetry exists, "we have a record of 0 of them" is true of
  // every skill and so tells the student nothing. The card says it once.
  if (!anyRecord) return solvedOf

  if (ev.solves_pasted > 0) {
    const n = ev.solves_pasted
    return `${solvedOf} · ${n} arrived as a single paste — worth revisiting ${
      n === 1 ? 'it' : 'them'
    } before your placement drive.`
  }

  const vouched = accountedFor(ev)
  if (vouched < ev.coding_solved) {
    // Explaining is the student's own doing, so it is credited to them rather
    // than described as something we observed. Saying "we have a record of how
    // you wrote it" about a solve they explained would be false, and would take
    // credit for their work besides.
    if (ev.solves_explained > 0) {
      return `${solvedOf} · you explained ${ev.solves_explained} of them. Explain more to account for the rest.`
    }
    // Says "we have no record", never "you did something". The gap is ours.
    return `${solvedOf} · we have a record of how you wrote ${vouched} of them.`
  }

  return solvedOf
}

/**
 * The bar splits at the share of the score we can account for.
 *
 * Deliberately proportional to the score rather than drawn as a second bar:
 * the shading qualifies the number being shown, it does not sit beside it as a
 * competing measurement.
 */
export function accountedWidth(score: number, ev: SkillEvidence): number {
  if (ev.coding_solved === 0) return score
  return (score * accountedFor(ev)) / ev.coding_solved
}

/**
 * The footnote under the whole card, or null when nothing needs explaining.
 *
 * Two different sentences for two different situations — conflating them is
 * how "we were not watching" turns into "you are under suspicion".
 */
export function cardFootnote(skills: HomeSkill[]): string | null {
  const anyRecord = hasAnyRecord(skills)
  const anySolves = skills.some((s) => (s.evidence?.coding_solved ?? 0) > 0)
  if (!anySolves) return null

  if (!anyRecord) {
    return 'We only started recording how solutions are written recently, so there is no record behind the work above. That is our gap, not a mark against you — solve a problem and answer the question afterwards to account for it yourself.'
  }
  if (skills.some((s) => s.evidence && needsCaveat(s.evidence, true))) {
    return 'Shaded means we have no record of how the code was written — including everything solved before we started keeping one. It is not a mark against you.'
  }
  return null
}
